import type { CardContentKind } from "#card-content";

/** The synced user settings that gate which card kinds enter the review rotation. */
export type CardReviewSettings = {
  /** Master switch for mic-requiring drills — see `user_table.isMicDrillsEnabled`. */
  isMicDrillsEnabled: boolean;
  isPinyinFlashcardsEnabled: boolean;
  isSentenceCharacterDrillEnabled: boolean;
  isSentenceSpokenDrillEnabled: boolean;
  isWordSentenceCardsEnabled: boolean;
};

/**
 * Build the eligibility settings from a (possibly absent) synced `user` row.
 *
 * Pronunciation and optional sentence/word cards stay out of
 * the rotation until the user's setting actually says otherwise. Callers use this instead of
 * assembling the object inline so adding a gated kind doesn't mean finding four call sites.
 */
export function cardReviewSettingsFromUser(
  user:
    | {
        isMicDrillsEnabled?: boolean | null;
        isPinyinFlashcardsEnabled?: boolean | null;
        isSentenceCharacterDrillEnabled?: boolean | null;
        isSentenceSpokenDrillEnabled?: boolean | null;
        isWordSentenceCardsEnabled?: boolean | null;
      }
    | null
    | undefined,
): CardReviewSettings {
  return {
    // Defaults TRUE: it means "the mic is usable", and a user who has
    // never touched it should not have mic drills silently withheld while their row syncs.
    isMicDrillsEnabled: user?.isMicDrillsEnabled ?? true,
    isPinyinFlashcardsEnabled: user?.isPinyinFlashcardsEnabled ?? false,
    isSentenceCharacterDrillEnabled: user?.isSentenceCharacterDrillEnabled ?? false,
    isSentenceSpokenDrillEnabled: user?.isSentenceSpokenDrillEnabled ?? false,
    isWordSentenceCardsEnabled: user?.isWordSentenceCardsEnabled ?? false,
  };
}

/**
 * Single source of truth for "is this kind of card eligible to be reviewed right now".
 *
 * Keyed by {@link CardContentKind} rather than by `DrillType` so it can be applied wherever a
 * card's kind is known — including the due-count badge, which reads actor overlay rows and
 * never builds a `DrillType`. Previously that badge re-expressed the rule as
 * `isPinyinFlashcardsEnabled || row.userGraphemePronunciationCard === null`, i.e. it inferred
 * "not a pronunciation card" from the *absence of an overlay*. That inference is wrong for any
 * third kind of card — which has no pronunciation overlay either, and so would be silently
 * treated as a meaning card and counted as always-eligible.
 *
 * Adding a kind means adding a branch here; the scheduler, the study-card list, and the badge
 * all read it, so they cannot drift apart.
 */
export function isCardContentKindReviewEligible({
  cardContentKind,
  settings,
}: {
  cardContentKind: CardContentKind;
  settings: CardReviewSettings;
}): boolean {
  switch (cardContentKind) {
    case "grapheme-meaning": {
      return true;
    }
    case "grapheme-pronunciation": {
      return settings.isPinyinFlashcardsEnabled;
    }
    case "word-characters-in-context": {
      return settings.isWordSentenceCardsEnabled;
    }
    case "word-characters-in-sentence": {
      return settings.isSentenceCharacterDrillEnabled;
    }
    case "word-pronunciation-in-sentence": {
      // BOTH gates: the user must want the drill, and the mic must be usable right now. The second
      // is what the drill's "Can't speak? Skip" escape flips, so skipping removes every mic card
      // from the queue for the rest of the session without discarding the preference.
      return settings.isSentenceSpokenDrillEnabled && settings.isMicDrillsEnabled;
    }
    case "quiz": {
      // Answering a quiz in its lesson is what enrolls it; nothing gates it out of reviews.
      return true;
    }
  }
}
